import { createContext, useContext, useEffect, useState } from "react";
import { DocLangSelect } from "../components/ClientDocExtras";

const VatLockCtx = createContext(false);
import { http, formatApiError } from "../lib/api";
import { MapPin, Check, Layers } from "lucide-react";
import { ClosedClientBanner, useClientRegistry, isBlockingClosed } from "../components/ClosedClient";
import { DateInput } from "../components/DateInput";
import { SearchSelect } from "../components/SearchSelect";

// Format des montants : 3 décimales partout (cohérent avec les tarifs HT).
export function fmtEUR(n) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(Number(n || 0));
}

// Format pour l'affichage non-monétaire (ex: PU sans symbole) — 3 décimales aussi.
export function fmt3(n) {
  if (n === "" || n === null || n === undefined) return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return num.toFixed(3);
}

// Format date "20 février 2026 à 14h30" pour un ISO string
function formatSignedAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const months = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} à ${hh}h${mm}`;
  } catch { return iso; }
}

export function statusBadge(kind, status) {
  const map = {
    devis: {
      draft: { l: "Brouillon", c: "bg-slate-100 text-slate-700 border-slate-300" },
      sent: { l: "Envoyé", c: "bg-sky-100 text-sky-800 border-sky-300" },
      accepted: { l: "Accepté", c: "bg-emerald-100 text-emerald-800 border-emerald-300" },
      rejected: { l: "Refusé", c: "bg-red-100 text-red-800 border-red-300" },
      cancelled: { l: "Annulé", c: "bg-slate-200 text-slate-600 border-slate-300" },
    },
    factures: {
      draft: { l: "Brouillon", c: "bg-slate-100 text-slate-700 border-slate-300" },
      sent: { l: "Envoyée", c: "bg-sky-100 text-sky-800 border-sky-300" },
      paid: { l: "Payée", c: "bg-emerald-100 text-emerald-800 border-emerald-300" },
      overdue: { l: "En retard", c: "bg-rose-100 text-rose-800 border-rose-300" },
      cancelled: { l: "Annulée", c: "bg-slate-200 text-slate-600 border-slate-300" },
    },
  };
  const s = map[kind]?.[status] || { l: status, c: "bg-slate-100 text-slate-700 border-slate-300" };
  return <span className={`inline-block rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${s.c}`}>{s.l}</span>;
}

// Groupe les lignes par site (mode multi-sites) : [{site|null, items:[{it, idx}]}]
// Échéance selon les conditions du client : 0 = à réception, mode "end_of_month" = N jours après la fin du mois
export function computeDue(dateStr, days, mode) {
  const d = new Date(`${dateStr}T12:00:00`);
  if (!days) return { due: dateStr, label: "À réception" };
  if (mode === "end_of_month") {
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 12);
    end.setDate(end.getDate() + days);
    return { due: end.toISOString().slice(0, 10), label: `${days} jours fin de mois` };
  }
  d.setDate(d.getDate() + days);
  return { due: d.toISOString().slice(0, 10), label: `${days} jours` };
}

export function groupItemsBySite(doc) {
  const items = (doc.items || []).map((it, idx) => ({ it, idx }));
  if (!doc.multi_site || !(doc.sites || []).length) return [{ site: null, items }];
  const ids = new Set(doc.sites.map((s) => s.site_id));
  const groups = doc.sites.map((site) => ({ site, items: items.filter((x) => x.it.site_id === site.site_id) }));
  const rest = items.filter((x) => !ids.has(x.it.site_id));
  if (rest.length) groups.push({ site: { site_id: null, site_name: "Autres prestations", site_address: "" }, items: rest });
  return groups;
}

const lineHT = (it) => (Number(it.quantity) || 0) * (Number(it.unit_price_ht) || 0);
const siteAddress = (s) => [s.address, [s.postal_code, s.city].filter(Boolean).join(" ")].filter(Boolean).join("\n");

export function InvoiceModal({ kind, value, onClose, onSaved, company }) {
  const isDevis = kind === "devis";
  const [v, setV] = useState(() => ({
    client_id: null,
    client_name: "",
    client_address: "",
    client_email: "",
    client_siret: "",
    client_siren: "",
    delivery_address: "",
    operation_category: "services",
    tva_regime: "encaissements",
    site_id: null,
    site_name: "",
    site_address: "",
    multi_site: false,
    sites: [],
    deposit_amount_ht: 0,
    deposit_amount_ttc: 0,
    deposit_percent: 0,
    deposit_paid: false,
    deposit_paid_date: "",
    deposit_notes: "",
    min_vacation_applied: false,
    min_vacation_hours: 6,
    min_vacation_waived: 0,
    period_start: "",
    period_end: "",
    date: new Date().toISOString().slice(0, 10),
    due_date: new Date(Date.now() + (company?.payment_terms_days ?? 40) * 864e5).toISOString().slice(0, 10),
    valid_until: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10),
    items: [{ description: "", quantity: 1, unit: "h", unit_price_ht: 0, vat_rate: company?.default_vat_rate ?? 20 }],
    notes: "",
    status: "draft",
    ...value,
    apply_min_floor: !(value?.min_vacation_waived > 0 && !value?.min_vacation_applied),
  }));
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    http.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    http.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  }, []);

  // Sites filtrés par client sélectionné
  const [registry, setRegistry] = useClientRegistry(v.client_id);
  const filteredSites = v.client_id ? sites.filter((s) => s.client_id === v.client_id) : sites;

  const onSelectSite = (id) => {
    if (!id) {
      setV({ ...v, site_id: null, site_name: "", site_address: "" });
      return;
    }
    const s = sites.find((x) => x.id === id);
    if (!s) return;
    setV({ ...v, site_id: s.id, site_name: s.name || "", site_address: siteAddress(s) });
  };

  const setMultiSite = (on) => {
    if (on) {
      const first = v.site_id ? [{ site_id: v.site_id, site_name: v.site_name, site_address: v.site_address }] : [];
      setV({ ...v, multi_site: true, sites: first, site_id: null, site_name: "", site_address: "",
        items: first.length ? v.items.map((it) => ({ ...it, site_id: first[0].site_id })) : v.items.filter((it) => it.description) });
    } else {
      setV({ ...v, multi_site: false, sites: [], items: v.items.map(({ site_id, ...it }) => it) });
    }
  };

  const toggleSite = (s) => {
    const selected = (v.sites || []).some((x) => x.site_id === s.id);
    if (!selected) {
      setV({ ...v, sites: [...(v.sites || []), { site_id: s.id, site_name: s.name || "", site_address: siteAddress(s) }],
        items: [...v.items.filter((it) => it.description || it.site_id), newItem(s.id)] });
      return;
    }
    const lines = v.items.filter((it) => it.site_id === s.id);
    if (lines.some((it) => it.description || it.unit_price_ht) && !window.confirm(`Retirer le site « ${s.name} » et ses ${lines.length} ligne(s) ?`)) return;
    setV({ ...v, sites: v.sites.filter((x) => x.site_id !== s.id), items: v.items.filter((it) => it.site_id !== s.id) });
  };

  const onSelectClient = (id) => {
    if (!id) {
      setV({ ...v, client_id: null });
      return;
    }
    const c = clients.find((x) => x.id === id);
    if (!c) return;
    const addrLines = [c.address, [c.postal_code, c.city].filter(Boolean).join(" "), c.country !== "France" ? c.country : ""]
      .filter(Boolean)
      .join("\n");
    const patch = {
      client_id: c.id,
      client_name: c.name || v.client_name,
      client_email: c.email || v.client_email,
      client_address: addrLines || v.client_address,
      client_siret: c.siret || v.client_siret,
      client_siren: c.siren || v.client_siren,
      doc_lang: c.doc_lang || "fr",
    };
    if (!isDevis && c.payment_terms_days != null) {
      const t = computeDue(v.date || new Date().toISOString().slice(0, 10), Number(c.payment_terms_days), c.payment_terms_mode);
      patch.due_date = t.due;
      patch.payment_terms_label = t.label;
    }
    if (v.multi_site) {
      const keep = (v.sites || []).filter((st) => sites.find((s) => s.id === st.site_id)?.client_id === c.id);
      patch.sites = keep;
    }
    // Réinitialiser le site si l'ancien ne fait pas partie du nouveau client
    if (v.site_id) {
      const currentSite = sites.find((s) => s.id === v.site_id);
      if (!currentSite || currentSite.client_id !== c.id) {
        patch.site_id = null;
        patch.site_name = "";
        patch.site_address = "";
      }
    }
    // Appliquer le taux de TVA du client à toutes les lignes (override si défini, sinon défaut société)
    const clientVat = c.vat_rate;
    const finalVat = clientVat === null || clientVat === undefined ? (company?.default_vat_rate ?? 20) : Number(clientVat);
    patch.items = (v.items || []).map((it) => ({ ...it, vat_rate: finalVat }));
    setV({ ...v, ...patch });
  };

  const total_ht = v.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price_ht) || 0), 0);
  const total_vat = v.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price_ht) || 0) * (Number(i.vat_rate) || 0) / 100, 0);
  const total_ttc = total_ht + total_vat;

  const save = async () => {
    if (isBlockingClosed(registry) && !window.confirm(`Établissement fermé dans l'annuaire des entreprises${registry.registry_name ? ` (${registry.registry_name})` : ""}.\n\nContinuer quand même ?`)) return;
    setSaving(true); setErr("");
    try {
      const path = isDevis ? "/devis" : "/factures";
      if (v.id) await http.put(`${path}/${v.id}`, v);
      else await http.post(path, v);
      onSaved(); onClose();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const selClient = clients.find((x) => x.id === v.client_id);
  const taxReg = selClient?.tax_regime;
  const foreign = selClient && (selClient.country || "France").trim().toLowerCase() !== "france";
  useEffect(() => {
    if (taxReg && v.items.some((it) => Number(it.vat_rate) !== 0)) setV((p) => ({ ...p, items: p.items.map((it) => ({ ...it, vat_rate: 0 })) }));
  }, [taxReg, v.items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lors de l'ajout d'une nouvelle ligne, utiliser le taux du client lié si disponible
  const defaultVatForNewItem = (() => {
    const c = clients.find((x) => x.id === v.client_id);
    if (c && c.vat_rate !== null && c.vat_rate !== undefined) return Number(c.vat_rate);
    return company?.default_vat_rate ?? 20;
  })();
  const newItem = (siteId) => ({ description: "", quantity: 1, unit: "h", unit_price_ht: 0, vat_rate: defaultVatForNewItem, ...(siteId ? { site_id: siteId } : {}) });
  const addItem = (siteId) => setV({ ...v, items: [...v.items, newItem(siteId)] });
  const updateItem = (i, patch) => setV({ ...v, items: v.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) });
  const removeItem = (i) => setV({ ...v, items: v.items.filter((_, idx) => idx !== i) });

  // Pré-remplissage automatique des lignes depuis les prestations du planning
  const [loadingLines, setLoadingLines] = useState(false);
  const [groupMode, setGroupMode] = useState("activity");
  const canAutoLoad = !!(v.client_id && v.period_start && v.period_end && (!v.multi_site || (v.sites || []).length));
  const fetchPreview = (siteId, floor) => http.post("/vacations/generate-devis", {
    start: v.period_start, end: v.period_end, client_id: v.client_id, site_id: siteId || null,
    assignment_filter: "all", group_mode: groupMode, preview_only: true, apply_min_floor: floor,
  });
  const loadPrestationsMulti = async (floor) => {
    const results = await Promise.all(v.sites.map((st) => fetchPreview(st.site_id, floor).catch(() => null)));
    if (results.some((r) => !r)) { setErr("Le calcul a échoué pour au moins un site : réessayez avant d'enregistrer."); return; }
    const items = results.flatMap((r, i) => (r.data.items || []).map((it) => ({
      description: it.description, quantity: Number(it.quantity) || 0, unit: it.unit || "h",
      unit_price_ht: Number(it.unit_price_ht) || 0, vat_rate: Number.isFinite(Number(it.vat_rate)) && it.vat_rate !== "" ? Number(it.vat_rate) : 20, site_id: v.sites[i].site_id,
    })));
    if (!items.length) { setErr("Aucune vacation trouvée pour ces sites sur cette période."); return; }
    const withMin = results.find((r) => r.data.min_vacation_applied);
    setV((cur) => ({ ...cur, items, notes: results.find((r) => r.data.notes)?.data.notes || cur.notes,
      min_vacation_applied: !!withMin, min_vacation_hours: withMin?.data.min_vacation_hours || 6,
      min_vacation_waived: results.reduce((n, r) => n + (r.data.min_vacation_waived || 0), 0) }));
  };
  const toggleFloor = (on) => {
    const reload = canAutoLoad && v.items.some((it) => it.description);
    if (reload && !window.confirm("Recalculer les lignes depuis le planning avec ce choix ?")) return;
    setV((cur) => ({ ...cur, apply_min_floor: on, ...(on ? {} : { min_vacation_applied: false }) }));
    if (reload) loadPrestations(on);
  };
  const loadPrestations = async (floor = v.apply_min_floor) => {
    if (!canAutoLoad) return;
    setLoadingLines(true); setErr("");
    if (v.multi_site) {
      try { await loadPrestationsMulti(floor); } catch (e) { setErr(formatApiError(e)); }
      finally { setLoadingLines(false); }
      return;
    }
    try {
      const r = await fetchPreview(v.site_id, floor);
      const items = (r.data.items || []).map((it) => ({
        description: it.description,
        quantity: Number(it.quantity) || 0,
        unit: it.unit || "h",
        unit_price_ht: Number(it.unit_price_ht) || 0,
        vat_rate: Number.isFinite(Number(it.vat_rate)) && it.vat_rate !== "" ? Number(it.vat_rate) : 20,
      }));
      if (items.length === 0) {
        setErr("Aucune vacation trouvée pour ce client sur cette période.");
      } else {
        setV((cur) => ({
          ...cur,
          items,
          notes: r.data.notes || cur.notes,
          min_vacation_applied: !!r.data.min_vacation_applied,
          min_vacation_hours: r.data.min_vacation_hours || 6,
          min_vacation_waived: r.data.min_vacation_waived || 0,
          // si un site avait été renvoyé (mode site unique), on synchronise les infos
          site_id: r.data.site_id || cur.site_id,
          site_name: r.data.site_name || cur.site_name,
          site_address: r.data.site_address || cur.site_address,
        }));
      }
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoadingLines(false); }
  };

  return (
    <VatLockCtx.Provider value={!!taxReg}>
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-4xl max-h-[95vh] overflow-auto border border-slate-200 animate-fade-in-up" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-bold">{v.id ? "Modifier" : "Nouveau"} {isDevis ? "devis" : "facture"}{v.number ? ` · ${v.number}`:""}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">✕</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <L label="Client enregistré">
            <SearchSelect
              data-testid="inv-client-select"
              className="input"
              value={v.client_id || ""}
              onChange={(e) => onSelectClient(e.target.value)}
            >
              <option value="">— Sélectionner un client (auto-remplir) —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </SearchSelect>
          </L>
          <ClosedClientBanner clientId={v.client_id} registry={registry} setRegistry={setRegistry} />
          {v.multi_site ? (
            <L label="Sites concernés">
              <div data-testid="inv-multi-site-summary" className="input bg-sky-50/60 text-sky-900 font-semibold">{(v.sites || []).length} site(s) sélectionné(s)</div>
            </L>
          ) : (
          <L label="Site concerné (facultatif)">
            <SearchSelect
              data-testid="inv-site-select"
              className="input"
              value={v.site_id || ""}
              onChange={(e) => onSelectSite(e.target.value)}
              disabled={!filteredSites.length}
            >
              <option value="">{`— Tous les sites${v.client_id ? " du client" : ""} —`}</option>
              {filteredSites.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </SearchSelect>
          </L>
          )}
          <div className="md:col-span-2">
            <SitePicker multi={!!v.multi_site} onToggleMulti={setMultiSite} sites={filteredSites} selected={v.sites || []} onToggleSite={toggleSite} hasClient={!!v.client_id} />
          </div>
          {taxReg && <div data-testid="inv-tax-regime" className="md:col-span-2 text-xs rounded px-3 py-2 border border-indigo-200 bg-indigo-50 text-indigo-900"><b>{taxReg === "reverse_charge" ? "Autoliquidation (client UE à TVA valide)" : "Client suisse : prestation non soumise à la TVA française (art. 259 CGI)"}</b> — TVA forcée à 0 % et mention légale traduite ajoutée sur le PDF.</div>}
          <L label="Client *"><input data-testid="inv-client" className="input" value={v.client_name} onChange={(e)=>setV({...v, client_name:e.target.value})}/></L>
          <L label="Email client"><input className="input" value={v.client_email || ""} onChange={(e)=>setV({...v, client_email:e.target.value})}/></L>
          <L label="Adresse"><input className="input" value={v.client_address || ""} onChange={(e)=>setV({...v, client_address:e.target.value})}/></L>
          <L label="Adresse de livraison (si différente)"><input data-testid="inv-delivery-address" className="input" placeholder={v.site_address && v.site_address !== v.client_address ? `Auto : ${v.site_address.replace(/\n/g, ", ")}` : "Vide = adresse du site d'intervention si différente"} value={v.delivery_address || ""} onChange={(e)=>setV({...v, delivery_address:e.target.value})}/></L>
          <L label={foreign ? "SIREN client (clients français uniquement)" : "SIREN client (9 chiffres) *"}><input className="input font-mono" maxLength={9} placeholder="123456789" value={v.client_siren || ""} onChange={(e)=>setV({...v, client_siren:e.target.value.replace(/\D/g,"")})}/></L>
          <L label="Langue du document"><DocLangSelect testid="inv-doc-lang" value={v.doc_lang} onChange={(doc_lang) => setV({ ...v, doc_lang })} /></L>
          <L label="SIRET client (14 chiffres)"><input className="input font-mono" value={v.client_siret || ""} onChange={(e)=>setV({...v, client_siret:e.target.value})}/></L>
          <L label={`Date ${isDevis ? "du devis" : "de facturation"} *`}><DateInput data-testid="doc-date" className="input" value={v.date} onChange={(e)=>setV({...v, date:e.target.value})}/></L>
          {isDevis
            ? <L label="Valable jusqu'au"><DateInput data-testid="doc-valid-until" className="input" value={v.valid_until || ""} onChange={(e)=>setV({...v, valid_until:e.target.value})}/></L>
            : <L label="Échéance"><DateInput data-testid="doc-due-date" className="input" value={v.due_date || ""} onChange={(e)=>setV({...v, due_date:e.target.value})}/></L>}
          <L label="Période de prestations — début"><DateInput data-testid="doc-period-start" className="input" value={v.period_start || ""} onChange={(e)=>setV({...v, period_start:e.target.value})}/></L>
          <L label="Période de prestations — fin"><DateInput data-testid="doc-period-end" className="input" value={v.period_end || ""} onChange={(e)=>setV({...v, period_end:e.target.value})}/></L>
          <L label="Catégorie d'opération">
            <select className="input" value={v.operation_category || "services"} onChange={(e)=>setV({...v, operation_category:e.target.value})}>
              <option value="services">Services</option>
              <option value="biens">Biens</option>
              <option value="mixte">Mixte (biens + services)</option>
            </select>
          </L>
          <L label="Régime TVA">
            <select className="input" value={v.tva_regime || "encaissements"} onChange={(e)=>setV({...v, tva_regime:e.target.value})}>
              <option value="encaissements">Sur les encaissements</option>
              <option value="debits">Sur les débits</option>
            </select>
          </L>
        </div>

        <div className="px-5 pb-4">
          {canAutoLoad && !v.id && (
            <div className="mb-3 border border-sky-200 bg-sky-50/60 rounded-md p-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                <div className="font-semibold text-slate-800">Pré-remplir depuis le planning</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  Charger automatiquement les prestations pour {v.multi_site ? <b>chacun des {v.sites.length} site(s) sélectionné(s)</b> : v.site_id ? <b>le site « {v.site_name} »</b> : <b>tous les sites du client</b>} du <b>{v.period_start}</b> au <b>{v.period_end}</b>.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  data-testid="inv-autoload-mode"
                  className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                  value={groupMode}
                  onChange={(e) => setGroupMode(e.target.value)}
                >
                  <option value="activity">Par activité</option>
                  <option value="day">Par jour</option>
                  <option value="full">Détail complet</option>
                </select>
                <button
                  type="button"
                  data-testid="inv-autoload-btn"
                  onClick={() => loadPrestations()}
                  disabled={loadingLines}
                  className="text-xs font-semibold rounded-md px-3 py-1.5 text-white disabled:opacity-60"
                  style={{ backgroundColor: "var(--brand-primary)" }}
                >
                  {loadingLines ? "…" : "Charger les prestations"}
                </button>
              </div>
            </div>
          )}
          {v.multi_site ? (
            <MultiSiteLines groups={groupItemsBySite(v)} onUpdate={updateItem} onRemove={removeItem} onAdd={addItem}
              totals={{ total_ht, total_vat, total_ttc }} />
          ) : (
          <>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Lignes</div>
            <button data-testid="inv-add-line" onClick={() => addItem()} className="text-xs font-semibold text-sky-700 hover:underline">+ Ajouter une ligne</button>
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <LinesHead />
              <tbody className="divide-y divide-slate-100">
                {v.items.map((it, i) => <ItemRow key={i} it={it} onChange={(p) => updateItem(i, p)} onRemove={() => removeItem(i)} />)}
                {v.items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-4 text-xs">Aucune ligne.</td></tr>}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr className="text-xs"><td colSpan={5} className="px-3 py-2 text-right font-semibold">Total HT</td><td className="px-2 py-2 text-right font-mono font-bold">{fmtEUR(total_ht)}</td><td/></tr>
                <tr className="text-xs"><td colSpan={5} className="px-3 py-2 text-right font-semibold">TVA</td><td className="px-2 py-2 text-right font-mono font-bold">{fmtEUR(total_vat)}</td><td/></tr>
                <tr className="text-sm bg-sky-50"><td colSpan={5} className="px-3 py-2 text-right font-display font-extrabold text-sky-900">Total TTC</td><td className="px-2 py-2 text-right font-mono font-extrabold text-sky-900">{fmtEUR(total_ttc)}</td><td/></tr>
              </tfoot>
            </table>
          </div>
          </>
          )}
        </div>

        <div className="px-5 pb-5">
          {/* Bloc Acompte */}
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50/40 p-3">
            <div className="text-xs font-bold text-amber-800 uppercase tracking-widest mb-2">
              {isDevis ? "Acompte demandé à la commande" : "Acompte déjà versé"}
            </div>
            <div className={`grid grid-cols-1 gap-3 ${isDevis ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
              <L label={isDevis ? "Montant HT (€)" : "Montant TTC (€)"}>
                <input
                  data-testid="inv-deposit-amount"
                  type="number" step="0.01" min="0"
                  className="input"
                  value={isDevis ? (v.deposit_amount_ht || 0) : (v.deposit_amount_ttc || 0)}
                  onChange={(e)=>{
                    const n = parseFloat(e.target.value) || 0;
                    setV({ ...v, ...(isDevis ? { deposit_amount_ht: n } : { deposit_amount_ttc: n }) });
                  }}
                  placeholder="0.00"
                />
              </L>
              {isDevis ? (
                <L label="ou en % du total HT">
                  <input
                    data-testid="inv-deposit-pct"
                    type="number" step="0.1" min="0" max="100"
                    className="input"
                    value={v.deposit_percent || 0}
                    onChange={(e)=>{ const p = parseFloat(e.target.value) || 0; setV({...v, deposit_percent: p, deposit_amount_ht: Math.round(total_ht * p) / 100}); }}
                    placeholder="30"
                  />
                </L>
              ) : (
                <L label="Date du versement">
                  <DateInput
                    data-testid="inv-deposit-date"
                    className="input"
                    value={v.deposit_paid_date || ""}
                    onChange={(e)=>setV({...v, deposit_paid_date: e.target.value})}
                  />
                </L>
              )}
              {!isDevis && (
                <L label="ou en % du total TTC">
                  <input
                    data-testid="inv-deposit-pct"
                    type="number" step="0.1" min="0" max="100"
                    className="input"
                    value={v.deposit_percent || 0}
                    onChange={(e)=>{ const p = parseFloat(e.target.value) || 0; setV({...v, deposit_percent: p, deposit_amount_ttc: Math.round(total_ttc * p) / 100}); }}
                  />
                </L>
              )}
              <L label={isDevis ? "Note (conditions)" : "Référence paiement"}>
                <input
                  data-testid="inv-deposit-notes"
                  className="input"
                  value={v.deposit_notes || ""}
                  onChange={(e)=>setV({...v, deposit_notes: e.target.value})}
                  placeholder={isDevis ? "Ex: à la signature" : "Ex: virement du 15/02"}
                />
              </L>
            </div>
            {!isDevis && (
              <label className="flex items-center gap-2 mt-2 text-xs text-slate-700 select-none">
                <input
                  data-testid="inv-deposit-paid"
                  type="checkbox"
                  checked={!!v.deposit_paid}
                  onChange={(e)=>setV({...v, deposit_paid: e.target.checked})}
                />
                <span>Acompte encaissé — le montant sera <b>déduit du total à régler</b>.</span>
              </label>
            )}
          </div>

          <label className="mb-3 flex items-start gap-2 text-xs text-slate-700 border border-blue-200 bg-blue-50/50 rounded-md p-3 select-none">
            <input
              data-testid="inv-min-vac"
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={v.apply_min_floor !== false}
              onChange={(e)=>toggleFloor(e.target.checked)}
            />
            <span>
              <b>Appliquer le plancher minimum de {v.min_vacation_hours || 6}h par vacation</b> (avenant IDCC 1351, sept. 2025) — oui : les vacations plus courtes (dont la case est cochée dans le planning) sont facturées sur {v.min_vacation_hours || 6}h ; non : facturation au temps réel, avec la mention « plancher non appliqué » sur le document.
              {v.min_vacation_waived > 0 && <span className="block mt-1 text-amber-700 font-semibold" data-testid="inv-min-vac-waived">Plancher non appliqué sur {v.min_vacation_waived} vacation(s) — mention ajoutée au document.</span>}
            </span>
          </label>
          <L label="Notes / conditions"><textarea rows={2} className="input" value={v.notes || ""} onChange={(e)=>setV({...v, notes:e.target.value})}/></L>
        </div>
        {err && <div className="mx-5 mb-5 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
          <button data-testid="inv-save" disabled={saving} onClick={save} className="px-4 py-2 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-md disabled:opacity-70">{saving?"…":"Enregistrer"}</button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
    </VatLockCtx.Provider>
  );
}

export function PrintView({ kind, doc, company, onClose, footer }) {
  const isDevis = kind === "devis";
  const logoUrl = company?.logo_file_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${company.logo_file_id}/download` : null;
  const [mentions, setMentions] = useState(footer || null);
  useEffect(() => { if (!footer) http.get("/settings/pdf-footer").then((r) => setMentions(r.data)).catch(() => {}); }, [footer]);
  const hasMentions = mentions && (mentions.fixed || mentions.custom);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 p-6 overflow-auto print:bg-white print:p-0">
      {/* Feuille A4 : 210mm × 297mm — proportions strictes, marge 18mm, footer en bas via flex */}
      <div
        className="a4-sheet mx-auto bg-white shadow-2xl print:shadow-none flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="a4-inner flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div className="flex gap-4 items-start">
              {logoUrl && <img src={logoUrl} alt="logo" className="h-28 object-contain"/>}
              <div>
                <div className="font-display text-2xl font-extrabold text-slate-900">{company?.name || "PRIME Sécurité"}</div>
                <div className="text-[11px] text-slate-600 mt-1.5 whitespace-pre-line leading-snug">{company?.legal_name ? company.legal_name + "\n" : ""}{company?.address}{company?.postal_code || company?.city ? `\n${company?.postal_code || ""} ${company?.city || ""}` : ""}</div>
                <div className="text-[11px] text-slate-600 mt-1">{company?.phone}{company?.email && ` · ${company.email}`}</div>
                <div className="text-[10px] text-slate-500 mt-1">{company?.siren && `SIREN: ${company.siren}`}{company?.siret && ` · SIRET: ${company.siret}`}{company?.tva_intracom && ` · TVA: ${company.tva_intracom}`}</div>
              </div>
            </div>
            <div className="text-right border-b-2 pb-2" style={{ borderColor: "var(--brand-primary)" }}>
              <div className="font-display text-3xl font-extrabold uppercase" style={{ color: "var(--brand-primary)" }}>{isDevis ? "Devis" : "Facture"}</div>
              <div className="font-mono text-sm text-slate-700 mt-1.5">N° {doc.number}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Date : {doc.date}</div>
              {isDevis && doc.valid_until && <div className="text-[11px] text-slate-500">Valable jusqu'au : {doc.valid_until}</div>}
              {!isDevis && doc.due_date && <div className="text-[11px] text-slate-500">Échéance : {doc.due_date}{doc.payment_terms_label ? ` (${doc.payment_terms_label})` : ""}</div>}
            </div>
          </div>

          {/* Client */}
          <div className="mb-5">
            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Client</div>
            <div className="text-sm font-semibold">{doc.client_name}</div>
            {doc.client_address && <div className="text-[11px] text-slate-600 whitespace-pre-line leading-snug">{doc.client_address}</div>}
            <div className="text-[10px] text-slate-500 mt-1">
              {doc.client_siren && `SIREN : ${doc.client_siren}`}
              {doc.client_siret && `${doc.client_siren ? " · " : ""}SIRET : ${doc.client_siret}`}
            </div>
            {(doc.delivery_address || (doc.site_address && doc.site_address.replace(/[\s,]+/g, " ").trim().toLowerCase() !== (doc.client_address || "").replace(/[\s,]+/g, " ").trim().toLowerCase())) && <div className="text-[10px] text-slate-500 mt-1" data-testid="preview-delivery">Livraison : {doc.delivery_address || doc.site_address.replace(/\n/g, ", ")}</div>}
          </div>

          {/* Métadonnées e-invoicing */}
          {(doc.period_start || doc.operation_category || doc.tva_regime) && (
            <div className="text-[10px] text-slate-600 mb-4 border-y border-slate-200 py-2 flex flex-wrap gap-x-4 gap-y-1">
              {doc.period_start && doc.period_end && <span><b>Période :</b> du {doc.period_start} au {doc.period_end}</span>}
              {doc.operation_category && <span><b>Catégorie :</b> {String(doc.operation_category).replace(/^./, (m)=>m.toUpperCase())}</span>}
              {doc.tva_regime && <span><b>Régime TVA :</b> {doc.tva_regime === "debits" ? "Sur les débits" : "Sur les encaissements"}</span>}
            </div>
          )}

          {/* Site concerné (juste avant le tableau des prestations) */}
          {!doc.multi_site && (doc.site_name || doc.site_address) && (
            <div className="mb-4 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2">
              <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Site concerné</div>
              <div className="text-sm font-semibold text-slate-900 mt-0.5">{doc.site_name || "—"}</div>
              {doc.site_address && <div className="text-[11px] text-slate-600 whitespace-pre-line leading-snug">{doc.site_address}</div>}
            </div>
          )}

          {/* Tableau prestations */}
          <table className="w-full text-[11px] mb-4">
            <thead className="border-b-2 border-slate-900 text-[9px] uppercase tracking-widest text-slate-600">
              <tr>
                <th className="py-1.5 text-left">Description</th>
                <th className="py-1.5">Qté</th>
                <th className="py-1.5">PU HT</th>
                <th className="py-1.5">TVA</th>
                <th className="py-1.5 text-right">Total HT</th>
              </tr>
            </thead>
            {groupItemsBySite(doc).map((g, gi) => (
              <tbody key={gi} className="divide-y divide-slate-100">
                {g.site && (
                  <tr className="bg-sky-50">
                    <td colSpan={5} className="py-2 px-2 border-l-4" style={{ borderLeftColor: "var(--brand-primary)" }}>
                      <span className="text-[8px] font-bold uppercase tracking-widest mr-2" style={{ color: "var(--brand-primary)" }}>{g.site.site_id ? `Site ${gi + 1}` : "Hors site"}</span>
                      <span className="font-bold text-slate-900">{g.site.site_name}</span>
                      {g.site.site_address && <div className="text-[10px] text-slate-600">{String(g.site.site_address).replace(/\n/g, " · ")}</div>}
                    </td>
                  </tr>
                )}
                {g.items.map(({ it, idx }) => (
                  <tr key={idx}>
                    <td className="py-1.5 pr-2">{it.description}</td>
                    <td className="py-1.5 text-center font-mono whitespace-nowrap">{it.quantity} {it.unit || ""}</td>
                    <td className="py-1.5 text-center font-mono whitespace-nowrap">{fmtEUR(it.unit_price_ht)}</td>
                    <td className="py-1.5 text-center font-mono">{it.vat_rate}%</td>
                    <td className="py-1.5 text-right font-mono font-semibold whitespace-nowrap">{fmtEUR(it.quantity * it.unit_price_ht)}</td>
                  </tr>
                ))}
                {g.site && (
                  <tr className="bg-slate-50">
                    <td colSpan={4} className="py-1.5 pr-2 text-right text-[10px] font-semibold text-slate-600">Sous-total HT — {g.site.site_name}</td>
                    <td className="py-1.5 text-right font-mono font-bold whitespace-nowrap">{fmtEUR(g.items.reduce((a, x) => a + lineHT(x.it), 0))}</td>
                  </tr>
                )}
              </tbody>
            ))}
          </table>

          {/* Totaux */}
          <div className="flex justify-end mb-4">
            <div className="w-64 text-[11px]">
              <div className="flex justify-between py-1"><span>Total HT</span><span className="font-mono font-semibold">{fmtEUR(doc.total_ht)}</span></div>
              <div className="flex justify-between py-1"><span>TVA</span><span className="font-mono font-semibold">{fmtEUR(doc.total_vat)}</span></div>
              <div
                className="flex justify-between py-1.5 border-t-2 border-slate-900 font-display text-base font-extrabold"
                style={{ color: "var(--brand-primary)" }}
              >
                <span>Total TTC</span><span className="font-mono">{fmtEUR(doc.total_ttc)}</span>
              </div>
              {/* Bloc acompte */}
              {(() => {
                const depHt = Number(doc.deposit_amount_ht || 0);
                const depTtcRaw = Number(doc.deposit_amount_ttc || 0);
                const ratio = doc.total_ht > 0 ? (doc.total_ttc / doc.total_ht) : 1;
                let depTtc;
                if (isDevis) {
                  if (!depHt || depHt <= 0) return null;
                  depTtc = depHt * ratio;
                  const pct = Number(doc.deposit_percent || 0);
                  return (
                    <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-[10px] text-amber-900">
                      <div className="font-bold">Acompte demandé à la commande</div>
                      <div className="flex justify-between mt-0.5"><span>{pct ? `${pct}% du HT` : "Montant"}</span><span className="font-mono font-semibold">{fmtEUR(depTtc)} TTC</span></div>
                      <div className="text-[9px] text-amber-800">({fmtEUR(depHt)} HT)</div>
                      {doc.deposit_notes && <div className="text-[9px] mt-0.5 italic">{doc.deposit_notes}</div>}
                    </div>
                  );
                }
                // Facture
                depTtc = depTtcRaw > 0 ? depTtcRaw : (depHt * ratio);
                if (depTtc <= 0) return null;
                if (doc.deposit_paid) {
                  const solde = (doc.total_ttc || 0) - depTtc;
                  return (
                    <>
                      <div className="flex justify-between py-1 text-emerald-700"><span>Acompte versé{doc.deposit_paid_date ? ` (${doc.deposit_paid_date})` : ""}</span><span className="font-mono font-semibold">- {fmtEUR(depTtc)}</span></div>
                      <div className="flex justify-between py-1.5 border-t-2 border-emerald-700 font-display text-base font-extrabold text-emerald-800"><span>Solde à régler</span><span className="font-mono">{fmtEUR(solde)}</span></div>
                      {doc.deposit_notes && <div className="text-[9px] text-slate-500 italic mt-1">{doc.deposit_notes}</div>}
                    </>
                  );
                }
                return null;
              })()}
            </div>
          </div>

          {/* Mention de signature électronique (devis signé via lien public) */}
          {isDevis && doc.signature?.signer_name && (
            <div className="mb-4 rounded-md border border-emerald-400 bg-emerald-50 px-4 py-3">
              <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-700">Signé électroniquement</div>
              <div className="text-[12px] text-emerald-900 mt-0.5">
                Par <b>{doc.signature.signer_name}</b>
                {doc.signature.signer_role ? <> ({doc.signature.signer_role})</> : null}
                {" "}le <b>{formatSignedAt(doc.signature.signed_at)}</b>
              </div>
              {doc.signature.email && (
                <div className="text-[10px] text-emerald-800 mt-0.5">
                  Email : {doc.signature.email}
                  {doc.signature.phone ? ` · Tél : ${doc.signature.phone}` : ""}
                </div>
              )}
              {doc.signature.otp_verified_at && (
                <div className="text-[10px] text-emerald-800">
                  Vérification code email : {formatSignedAt(doc.signature.otp_verified_at)}
                </div>
              )}
              <div className="text-[9px] text-emerald-700 mt-0.5">
                Méthode : {doc.signature.method === "OTP_email_click_wrap" ? "Code email (OTP) + Bon pour accord" : "Bon pour accord (click-wrap)"}
                {doc.signature.ip ? ` · IP ${doc.signature.ip}` : ""}
                {Array.isArray(doc.signature.access_log) && doc.signature.access_log.length ? ` · Accès(s) : ${doc.signature.access_log.length}` : ""}
              </div>
            </div>
          )}

          {doc.notes && <div className="text-[10px] text-slate-600 border-t border-slate-200 pt-3">{doc.notes}</div>}

          {doc.min_vacation_waived > 0 && (
            <div className="mt-3 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-[10px] leading-snug text-amber-900" data-testid="doc-min-vac-waived">
              <b>Plancher minimum de facturation non appliqué</b> — le plancher de {doc.min_vacation_hours || 6}h par vacation (IDCC 1351) n'a pas été appliqué à {doc.min_vacation_waived} vacation(s) d'une durée inférieure : ces prestations sont facturées au temps réellement effectué.
            </div>
          )}
          {/* Mention légale du plancher minimum de facturation (avenant IDCC 1351) */}
          {doc.min_vacation_applied && (
            <div className="mt-3 rounded-md border border-blue-400 bg-blue-50 px-3 py-2 text-[10px] leading-snug text-blue-900">
              <div className="font-bold text-[10px] mb-0.5">Plancher minimum de facturation ({doc.min_vacation_hours || 6}h par vacation)</div>
              <div>
                Conformément à l'avenant n° 2 du 3 septembre 2025 à l'accord de branche de la Sécurité Privée
                (IDCC 1351) étendu par arrêté ministériel, toute vacation commencée ou planifiée est soumise à un plancher
                minimal obligatoire de rémunération et de facturation équivalent à {doc.min_vacation_hours || 6} heures de travail effectif.
                Les prestations d'une durée inférieure seront facturées sur cette base forfaitaire minimale de {doc.min_vacation_hours || 6} heures.
              </div>
            </div>
          )}
          {!isDevis && company?.iban && <div className="text-[10px] text-slate-600 mt-2">Règlement par virement : IBAN {company.iban}{company.bic ? ` / BIC ${company.bic}` : ""}</div>}
          {!isDevis && (
            <div className="text-[9px] text-slate-500 mt-2 leading-snug">
              Pénalités de retard : 3× taux légal · Indemnité forfaitaire de recouvrement : 40 €.
              Escompte pour paiement anticipé : néant sauf mention contraire.
            </div>
          )}

          {/* Spacer pour pousser le footer en bas */}
          <div className="flex-1" />

          {/* Pied-de-page tout en bas */}
          <div className="pt-3 mt-3 border-t border-slate-200 text-[9px] text-slate-500 text-center whitespace-pre-line leading-snug">
            {hasMentions ? <><div data-testid="print-footer-fixed" className="font-bold text-slate-700">{mentions.fixed}</div>{mentions.custom && <div data-testid="print-footer-custom">{mentions.custom}</div>}</>
              : (company?.footer_notes || `${company?.legal_name || company?.name || ""}${company?.siret ? ` · SIRET ${company.siret}` : ""}${company?.tva_intracom ? ` · TVA ${company.tva_intracom}` : ""}`)}
          </div>
        </div>
      </div>

      {/* Actions (hors feuille, cachées à l'impression) */}
      <div className="max-w-[210mm] mx-auto flex justify-end gap-2 mt-4 print:hidden">
        <button onClick={()=>window.print()} className="px-4 py-2 text-sm font-semibold bg-slate-900 text-white rounded-md hover:bg-slate-800">Imprimer / PDF</button>
        <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 bg-white rounded-md hover:bg-slate-100">Fermer</button>
      </div>

      <style>{`
        .a4-sheet { width: 210mm; min-height: 297mm; box-sizing: border-box; }
        .a4-inner { padding: 18mm; min-height: 297mm; box-sizing: border-box; }
        @media print {
          @page { size: A4; margin: 0; }
          html, body { background: white !important; }
          .a4-sheet { width: 210mm; min-height: 297mm; margin: 0; box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}

function L({ label, children }) { return <label className="block"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>; }

const cellInput = "w-full border-0 bg-slate-50 rounded px-1.5 py-1 text-xs focus:ring-2 focus:ring-sky-500";

function LinesHead() {
  return (
    <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
      <tr><th className="px-2 py-2 text-left font-bold">Description</th><th className="px-2 py-2 font-bold">Qté</th><th className="px-2 py-2 font-bold">Unité</th><th className="px-2 py-2 font-bold">PU HT</th><th className="px-2 py-2 font-bold">TVA%</th><th className="px-2 py-2 font-bold text-right">Total HT</th><th></th></tr>
    </thead>
  );
}

function ItemRow({ it, onChange, onRemove }) {
  const lockVat = useContext(VatLockCtx);
  const num = (k) => (e) => onChange({ [k]: parseFloat(e.target.value || 0) });
  return (
    <tr>
      <td className="px-2 py-1.5"><input data-testid="inv-line-desc" className={cellInput} value={it.description} onChange={(e) => onChange({ description: e.target.value })} /></td>
      <td className="px-2 py-1.5 w-20"><input type="number" step="0.001" className={`${cellInput} text-right font-mono`} value={it.quantity} onChange={num("quantity")} /></td>
      <td className="px-2 py-1.5 w-16"><input className={cellInput} value={it.unit || ""} onChange={(e) => onChange({ unit: e.target.value })} /></td>
      <td className="px-2 py-1.5 w-28"><input data-testid="inv-line-pu" type="number" step="0.001" className={`${cellInput} text-right font-mono`} value={it.unit_price_ht} onChange={num("unit_price_ht")} /></td>
      <td className="px-2 py-1.5 w-16"><input data-testid="inv-line-vat" type="number" step="0.1" disabled={lockVat} title={lockVat ? "TVA 0 % imposée (autoliquidation / art. 259 CGI)" : undefined} className={`${cellInput} text-right font-mono disabled:bg-slate-100`} value={it.vat_rate} onChange={num("vat_rate")} /></td>
      <td className="px-2 py-1.5 text-right font-mono font-semibold">{fmtEUR(lineHT(it))}</td>
      <td className="px-2 py-1.5"><button onClick={onRemove} className="text-rose-600 hover:underline text-xs">×</button></td>
    </tr>
  );
}

function SitePicker({ multi, onToggleMulti, sites, selected, onToggleSite, hasClient }) {
  const ids = new Set(selected.map((s) => s.site_id));
  return (
    <div className={`rounded-md border p-3 transition-colors ${multi ? "border-sky-300 bg-sky-50/50" : "border-slate-200"}`}>
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 select-none cursor-pointer">
        <input data-testid="inv-multi-site-toggle" type="checkbox" className="h-4 w-4" checked={multi} onChange={(e) => onToggleMulti(e.target.checked)} />
        <Layers className="h-4 w-4 text-sky-700" /> Devis / facture sur plusieurs sites
      </label>
      {multi && (
        <div className="mt-3">
          {!hasClient && <div className="text-xs text-amber-700">Sélectionnez d'abord un client pour afficher ses sites.</div>}
          {hasClient && !sites.length && <div className="text-xs text-slate-500">Ce client n'a aucun site enregistré.</div>}
          <div className="flex flex-wrap gap-2">
            {sites.map((s) => {
              const on = ids.has(s.id);
              return (
                <button type="button" key={s.id} data-testid={`inv-site-chip-${s.id}`} onClick={() => onToggleSite(s)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-sky-400"}`}>
                  {on ? <Check className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}{s.name}
                  {s.city && <span className={`font-normal ${on ? "text-sky-100" : "text-slate-400"}`}>· {s.city}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MultiSiteLines({ groups, onUpdate, onRemove, onAdd, totals }) {
  const real = groups.filter((g) => g.site);
  return (
    <div className="space-y-4" data-testid="inv-multi-site-lines">
      {!real.length && <div className="text-center text-sm text-slate-500 border border-dashed border-slate-300 rounded-md py-6">Sélectionnez un ou plusieurs sites ci-dessus pour ajouter leurs lignes.</div>}
      {real.map((g, gi) => (
        <SiteBlock key={g.site.site_id || "rest"} index={gi + 1} group={g} onUpdate={onUpdate} onRemove={onRemove} onAdd={onAdd} />
      ))}
      {real.length > 0 && (
        <div className="flex justify-end">
          <div className="w-72 text-xs rounded-md border border-slate-200 overflow-hidden" data-testid="inv-multi-totals">
            {real.map((g) => (
              <div key={g.site.site_id || "rest"} className="flex justify-between px-3 py-1.5 text-slate-600 border-b border-slate-100">
                <span className="truncate pr-2">{g.site.site_name}</span><span className="font-mono">{fmtEUR(g.items.reduce((a, x) => a + lineHT(x.it), 0))}</span>
              </div>
            ))}
            <div className="flex justify-between px-3 py-1.5 font-semibold bg-slate-50"><span>Total HT</span><span className="font-mono">{fmtEUR(totals.total_ht)}</span></div>
            <div className="flex justify-between px-3 py-1.5 font-semibold bg-slate-50"><span>TVA</span><span className="font-mono">{fmtEUR(totals.total_vat)}</span></div>
            <div className="flex justify-between px-3 py-2 font-display font-extrabold text-sm bg-sky-50 text-sky-900"><span>Total TTC</span><span className="font-mono">{fmtEUR(totals.total_ttc)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

function SiteBlock({ index, group, onUpdate, onRemove, onAdd }) {
  const { site, items } = group;
  const sub = items.reduce((a, x) => a + lineHT(x.it), 0);
  return (
    <div className="rounded-md border border-slate-200 overflow-hidden border-l-4" style={{ borderLeftColor: "var(--brand-primary)" }} data-testid={`inv-site-block-${site.site_id || "rest"}`}>
      <div className="flex items-start justify-between gap-3 px-3 py-2.5 bg-sky-50/70">
        <div className="min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-widest text-sky-700">{site.site_id ? `Site ${index}` : "Hors site"}</div>
          <div className="text-sm font-bold text-slate-900">{site.site_name}</div>
          {site.site_address && <div className="text-[11px] text-slate-600 whitespace-pre-line leading-snug">{site.site_address}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Sous-total HT</div>
          <div className="font-mono font-bold text-sky-900">{fmtEUR(sub)}</div>
        </div>
      </div>
      <table className="w-full text-xs">
        <LinesHead />
        <tbody className="divide-y divide-slate-100">
          {items.map(({ it, idx }) => <ItemRow key={idx} it={it} onChange={(p) => onUpdate(idx, p)} onRemove={() => onRemove(idx)} />)}
          {!items.length && <tr><td colSpan={7} className="text-center text-slate-400 py-3 text-xs">Aucune ligne pour ce site.</td></tr>}
        </tbody>
      </table>
      {site.site_id && (
        <div className="px-3 py-2 border-t border-slate-100">
          <button data-testid={`inv-add-line-site-${site.site_id}`} onClick={() => onAdd(site.site_id)} className="text-xs font-semibold text-sky-700 hover:underline">+ Ajouter une ligne pour ce site</button>
        </div>
      )}
    </div>
  );
}
